import React, { useEffect, useMemo, useState } from 'react';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { useToast } from '../../hooks/useToast';
import {
  useGetJobOrdersQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
  usePresignOrderImageUploadMutation,
  useCreateOrderImageMutation,
  useGetJobQuery,
  useGetGlassValuesDropdownQuery,
} from '../../store/slices/apiSlice';
import type { ProductType, OrderFormData, OrderSummary, OrdersEditPanelProps } from '../../types';
import { supportsRoundGlass } from '../../utils/orderRoundGlass';
import { FileUploadCard } from '../../components/common/FileUploadCard';
import { SearchableGlassValueSelect } from '../../components/common/SearchableGlassValueSelect';
import { PatternedGlassOptionSelect } from '../../components/common/PatternedGlassOptionSelect';
import { OrderDetailsTable } from '../../components/common/OrderDetailsTable';
import { MoveToProductionButton } from '../../components/common/MoveToProductionButton';
// import { useDispatch } from 'react-redux';

function normalizeOrderMetadataForEdit(metadata: Record<string, unknown> | undefined): Record<string, unknown> {
  const m = { ...(metadata || {}) };
  const three = m.glass_side_three_value_id ?? m.middle_glass_panel_value_id;
  if (three) {
    m.glass_side_three_value_id = three;
    m.middle_glass_panel_value_id = three;
  }
  return m;
}

function buildOrderMetadataForSave(
  metadata: Record<string, unknown> | undefined,
  productType: ProductType,
): Record<string, unknown> {
  const m = { ...(metadata || {}) };
  if (productType === 'triple' && m.glass_side_three_value_id) {
    m.middle_glass_panel_value_id = m.glass_side_three_value_id;
  }
  return m;
}

export const OrdersEditPanel: React.FC<OrdersEditPanelProps> = ({ jobId }) => {
  const toast = useToast();
  const { data: ordersData, isLoading: isOrdersLoading } = useGetJobOrdersQuery(jobId);
  const { data: job } = useGetJobQuery(jobId);
  const [createOrder, { isLoading: isCreating }] = useCreateOrderMutation();
  const [updateOrder, { isLoading: isUpdatingOrder }] = useUpdateOrderMutation();
  const [deleteOrder] = useDeleteOrderMutation();
  const [presignUpload] = usePresignOrderImageUploadMutation();
  const [createOrderImage] = useCreateOrderImageMutation();

  const [productType, setProductType] = useState<ProductType | null>(null);
  const [form, setForm] = useState<Partial<OrderFormData>>({ quantity: 1 });
  const [cutoutShape, setCutoutShape] = useState<'circle'|'square'|'socket'|''>('');
  const [showDrilled, setShowDrilled] = useState<boolean>(false);
  const [showCutouts, setShowCutouts] = useState<boolean>(false);
  const [showRounded, setShowRounded] = useState<boolean>(false);
  const [showDecorative, setShowDecorative] = useState<boolean>(false);
  const [showDuplex, setShowDuplex] = useState<boolean>(false);
  const [showGeorgian, setShowGeorgian] = useState<boolean>(false);
  const [showQuoteDrawing, setShowQuoteDrawing] = useState<boolean>(false);

  // Image toggles (Phase 4.A)
  const [hasDrilledImage, setHasDrilledImage] = useState<'no'|'yes'>('no');
  const [hasCutoutImage, setHasCutoutImage] = useState<'no'|'yes'>('no');
  const [hasRoundedImage, setHasRoundedImage] = useState<'no'|'yes'>('no');
  const [hasDecorativeImage, setHasDecorativeImage] = useState<'no'|'yes'>('no');
  const [hasDuplexImage, setHasDuplexImage] = useState<'no'|'yes'>('no');
  const [hasGeorgianImage, setHasGeorgianImage] = useState<'no'|'yes'>('no');
  const [hasQuoteDrawingImage, setHasQuoteDrawingImage] = useState<'no'|'yes'>('no');
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Store pending images before order is saved
  const [pendingImages, setPendingImages] = useState<{
    drilled_holes?: File;
    cutout?: File;
    rounded_corners?: File;
    decorative_leads?: File;
    duplex_bar?: File;
    georgian_bar?: File;
    quote_drawing?: File;
  }>({});

  // When switching to Double, ensure single-only flags are cleared
  useEffect(() => {
    if (productType === 'double') {
      setForm(f => ({ ...f, polished_edges: false, dubbed_corners: false }));
      setShowDrilled(false);
      setShowRounded(false);
    }
  }, [productType]);

  // Note: Image functionality is temporarily disabled in the reusable table
  // Will be re-implemented in a future update

  // Note: Image-related useEffect temporarily disabled for reusable table implementation

  const orders: OrderSummary[] = Array.isArray(ordersData) ? ordersData : (ordersData?.results ?? []);

  const { data: glassDropdown } = useGetGlassValuesDropdownQuery(
    { product_type: productType ?? undefined },
    { skip: !productType },
  );

  const sideSupportsPatterned = useMemo(() => {
    return (glassId: string | null | undefined) => {
      if (!glassId || !glassDropdown?.results) return false;
      const o = glassDropdown.results.find((x) => x.value === glassId);
      return !!(o && o.supports_patterned_options);
    };
  }, [glassDropdown?.results]);

  const orderMeta = useMemo(() => (form.metadata || {}) as Record<string, unknown>, [form.metadata]);

  // Temporary placeholder for image functionality
  const getExistingImage = (_feature: string) => null;

  // Helper to upload a single image
  const uploadImage = async (orderId: string, file: File, feature: string, imageType: 'drawing' | 'cutout') => {
    // 1) Get presigned URL
    const presignData = await presignUpload({
      orderId,
      content_type: file.type || 'application/octet-stream',
      file_size: file.size,
      image_type: imageType,
      feature: feature as any
    }).unwrap();
    
    // 2) Upload to S3
    const formData = new FormData();
    Object.entries(presignData.fields).forEach(([k, v]) => formData.append(k, v as string));
    formData.append('file', file);
    
    const uploadResponse = await fetch(presignData.url, {
      method: 'POST',
      body: formData
    });
    
    if (!uploadResponse.ok) {
      throw new Error('S3 upload failed');
    }
    
    // 3) Create DB record
    await createOrderImage({
      orderId,
      body: {
        s3_key: presignData.key,
        content_type: file.type || 'application/octet-stream',
        file_size: file.size,
        image_type: imageType,
        feature: feature as any
      }
    }).unwrap();
  };

  // Derived options for spacer color selects (avoids conditional fragments inside <select>)
  // Show all standard colours (backend allows any colour with any type per request)
  const allSpacerColors = ['silver', 'black', 'white', 'grey'];

  // Action handlers for the OrderDetailsTable
  const handleEditOrder = async (order: OrderSummary) => {
    setEditingId(order.id);
    setProductType((order.product_type || 'single') as ProductType);
    setForm({
      sku: order.sku ?? null,
      glass_width: (order as any).glass_width ?? null,
      glass_height: (order as any).glass_height ?? null,
      round_glass: !!(order as any).round_glass || !!(order as any).metadata?.woo_diameter,
      diameter_mm: (order as any).diameter_mm ?? ((order as any).metadata?.woo_diameter != null ? Number((order as any).metadata.woo_diameter) : null),
      quantity: order.quantity,
      // rack: (order as any).rack ?? null, // managed via inline table editing
      reference: order.reference ?? null,
      notes: (order as any).notes ?? null,
      unit_price: (order as any).unit_price ?? '0.00',
      // single glazed fields
      glass_side_one: (order as any).glass_side_one ?? null,
      polished_edges: !!(order as any).polished_edges,
      dubbed_corners: !!(order as any).dubbed_corners,
      hole_size_mm: (order as any).hole_size_mm ?? null,
      hole_count: (order as any).hole_count ?? null,
      hole_locations: (order as any).hole_locations ?? null,
      cutout_shape: (order as any).cutout_shape ?? null,
      cutout_count: (order as any).cutout_count ?? null,
      cutout_diameter_mm: (order as any).cutout_diameter_mm ?? null,
      cutout_width_mm: (order as any).cutout_width_mm ?? null,
      cutout_height_mm: (order as any).cutout_height_mm ?? null,
      cutout_position: (order as any).cutout_position ?? null,
      rounded_corner_radius_mm: (order as any).rounded_corner_radius_mm ?? null,
      rounded_corner_count: (order as any).rounded_corner_count ?? null,
      rounded_corner_locations: (order as any).rounded_corner_locations ?? null,
      // double glazed fields
      spacer_bar_type: (order as any).spacer_bar_type ?? null,
      spacer_bar_color: (order as any).spacer_bar_color ?? null,
      spacer_bar_thickness_mm: (order as any).spacer_bar_thickness_mm ?? null,
      argon_filled: !!(order as any).argon_filled,
      // triple glazed fields
      middle_glass_panel: (order as any).middle_glass_panel ?? null,
      spacer_bar2_type: (order as any).spacer_bar2_type ?? null,
      spacer_bar2_color: (order as any).spacer_bar2_color ?? null,
      spacer_bar2_thickness_mm: (order as any).spacer_bar2_thickness_mm ?? null,
      // mirror fields
      aluminium_backing_foil: !!(order as any).aluminium_backing_foil,
      safety_backing: !!(order as any).safety_backing,
      // decorative leads fields
      decorative_leaded_pattern: (order as any).decorative_leaded_pattern ?? null,
      decorative_lead_color: (order as any).decorative_lead_color ?? null,
      decorative_lead_thickness_mm: (order as any).decorative_lead_thickness_mm ?? null,
      decorative_lead_soldering_required: !!(order as any).decorative_lead_soldering_required,
      // Load glass value selections from metadata (align side-3 keys for edit form + table)
      metadata: normalizeOrderMetadataForEdit((order as any).metadata),
    });
    setCutoutShape(((order as any).cutout_shape as any) || '');
    setShowDrilled(Boolean((order as any).hole_size_mm || (order as any).hole_count || (order as any).hole_locations));
    setShowCutouts(Boolean((order as any).cutout_shape || (order as any).cutout_count || (order as any).cutout_diameter_mm || (order as any).cutout_width_mm || (order as any).cutout_height_mm || (order as any).cutout_position));
    setShowRounded(Boolean((order as any).rounded_corner_radius_mm || (order as any).rounded_corner_count || (order as any).rounded_corner_locations));
    setShowDecorative(Boolean((order as any).decorative_leaded_pattern || (order as any).decorative_lead_color || (order as any).decorative_lead_thickness_mm || (order as any).decorative_lead_soldering_required));
  };

  const handleDeleteOrder = async (order: OrderSummary) => {
    if (!confirm('Delete this order?')) return;
    try {
      await deleteOrder({ id: order.id, jobId }).unwrap();
      toast.success('Order deleted', 'The order has been deleted.');
      if (editingId === order.id) resetForm();
    } catch (e: any) { 
      toast.error('Delete failed', e?.data?.detail || 'Please try again.'); 
    }
  };

  const onSave = async () => {
    if (!productType) { toast.info('Product type required', 'Please select a product type.'); return; }
    const payload: OrderFormData = {
      product_type: productType,
      quantity: form.quantity ?? 1,
      sku: form.sku ?? null,
      glass_width: form.round_glass ? null : (form.glass_width ?? null),
      glass_height: form.round_glass ? null : (form.glass_height ?? null),
      round_glass: supportsRoundGlass(productType) ? !!form.round_glass : false,
      diameter_mm: supportsRoundGlass(productType) && form.round_glass ? (form.diameter_mm ?? null) : null,
      // rack: form.rack ?? null, // managed via inline table editing
      reference: form.reference ?? null,
      notes: form.notes ?? null,
      unit_price: form.unit_price ?? '0.00',
      // single glazed extras
      glass_side_one: form.glass_side_one ?? null,
      polished_edges: !!form.polished_edges,
      dubbed_corners: !!form.dubbed_corners,
      hole_size_mm: form.hole_size_mm ?? null,
      hole_count: form.hole_count ?? null,
      hole_locations: form.hole_locations ?? null,
      cutout_shape: (form.cutout_shape as any) ?? null,
      cutout_count: form.cutout_count ?? null,
      cutout_diameter_mm: form.cutout_diameter_mm ?? null,
      cutout_width_mm: form.cutout_width_mm ?? null,
      cutout_height_mm: form.cutout_height_mm ?? null,
      cutout_position: form.cutout_position ?? null,
      rounded_corner_radius_mm: form.rounded_corner_radius_mm ?? null,
      rounded_corner_count: form.rounded_corner_count ?? null,
      rounded_corner_locations: form.rounded_corner_locations ?? null,
      // double glazed extras
      spacer_bar_type: form.spacer_bar_type ?? null,
      spacer_bar_color: form.spacer_bar_color ?? null,
      spacer_bar_thickness_mm: form.spacer_bar_thickness_mm ?? null,
      // triple glazed extras
      middle_glass_panel: (form.middle_glass_panel as any) ?? null,
      spacer_bar2_type: (form.spacer_bar2_type as any) ?? null,
      spacer_bar2_color: (form.spacer_bar2_color as any) ?? null,
      spacer_bar2_thickness_mm: form.spacer_bar2_thickness_mm ?? null,
      argon_filled: !!form.argon_filled,
      // mirror extras
      aluminium_backing_foil: !!form.aluminium_backing_foil,
      safety_backing: !!form.safety_backing,
      decorative_leaded_pattern: form.decorative_leaded_pattern ?? null,
      decorative_lead_color: form.decorative_lead_color ?? null,
      decorative_lead_thickness_mm: form.decorative_lead_thickness_mm ?? null,
      decorative_lead_soldering_required: !!form.decorative_lead_soldering_required,
      duplex_bar_type: form.duplex_bar_type ?? null,
      duplex_bar_thickness: form.duplex_bar_thickness ?? null,
      georgian_bar_type: form.georgian_bar_type ?? null,
      georgian_bar_thickness: form.georgian_bar_thickness ?? null,
      // Include glass value selections in metadata; keep middle_glass_panel_value_id in sync for triple
      metadata: buildOrderMetadataForSave(form.metadata as Record<string, unknown> | undefined, productType),
    } as OrderFormData;
    try {
      if (editingId) {
        const result = await updateOrder({ id: editingId, data: payload }).unwrap();
        if (result.argon_filled !== undefined) {
          setForm((f) => ({ ...f, argon_filled: !!result.argon_filled }));
        }
        toast.success('Order updated', 'The order has been updated.');
      } else {
        const result = await createOrder({ jobId, data: payload }).unwrap();
        
        // Upload any pending images
        const uploadPromises = [] as Array<Promise<any>>;
        for (const [feature, file] of Object.entries(pendingImages)) {
          if (file) {
            const imageType = feature === 'cutout' ? 'cutout' : 'drawing';
            uploadPromises.push(uploadImage(result.id, file, feature as any, imageType));
          }
        }
        
        if (uploadPromises.length > 0) {
          toast.info('Uploading images...', 'Please wait while images are uploaded.');
          try {
            await Promise.all(uploadPromises);
            toast.success('Order created', 'Order and images have been saved successfully.');
          } catch (e) {
            toast.warning('Order created', 'Order saved but some images failed to upload.');
          }
        } else {
          toast.success('Order created', 'The order has been created.');
        }
        
        resetForm();
      }
    } catch (e: any) {
      toast.error('Save failed', e?.data?.detail || 'Please check fields and try again.');
    }
  };

  const resetForm = () => {
    setForm({ quantity: 1, metadata: {} });
    setProductType(null);
    setEditingId(null);
    setCutoutShape('');
    setShowDrilled(false);
    setShowCutouts(false);
    setShowRounded(false);
    setShowDecorative(false);
    setShowDuplex(false);
    setShowGeorgian(false);
    setShowQuoteDrawing(false);
    setHasDrilledImage('no');
    setHasCutoutImage('no');
    setHasRoundedImage('no');
    setHasDecorativeImage('no');
    setHasDuplexImage('no');
    setHasGeorgianImage('no');
    setHasQuoteDrawingImage('no');
    setPendingImages({});
  };

  const TypeChip: React.FC<{ value: ProductType; label: string }> = ({ value, label }) => (
    <button
      onClick={() => {
        setProductType(value);
        if (!supportsRoundGlass(value)) {
          setForm(f => ({ ...f, round_glass: false, diameter_mm: null }));
        }
      }}
      className={`px-3 py-1 rounded-full border text-sm mr-2 mb-2 ${productType === value ? 'bg-blue-600 text-white border-blue-700' : 'bg-gray-100 text-gray-800 border-gray-300'}`}
    >{label}</button>
  );

  return (
    <ContentCard title="Orders">
      <div className="mb-4">
        <div className="text-sm font-medium mb-2">Product Type (required)</div>
        <div>
          <TypeChip value="single" label="Single Glazed" />
          <TypeChip value="double" label="Double Glazed" />
          <TypeChip value="triple" label="Triple Glazed" />
          <TypeChip value="mirror" label="Mirror" />
        </div>
      </div>

      {/* Width/Height moved into first card below */}
      {/* SKU hidden as requested */}
      {/* Single/Double/Triple/Mirror shared form */}
      {!!productType && (
        <div className="mt-4 space-y-4">
          <ContentCard title="Glass Specifications">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {!form.round_glass && (
                <>
                  <div>
                    <label className="form-label">Glass Width (mm)</label>
                    <input className="form-input max-w-[200px]" type="number" value={form.glass_width ?? ''}
                      onChange={e => setForm(f => ({ ...f, glass_width: e.target.value ? Number(e.target.value) : null }))} placeholder="e.g. 800" />
                  </div>
                  <div>
                    <label className="form-label">Glass Height (mm)</label>
                    <input className="form-input max-w-[200px]" type="number" value={form.glass_height ?? ''}
                      onChange={e => setForm(f => ({ ...f, glass_height: e.target.value ? Number(e.target.value) : null }))} placeholder="e.g. 1200" />
                  </div>
                </>
              )}
              {supportsRoundGlass(productType) && !!form.round_glass && (
                <div>
                  <label className="form-label">Diameter (mm)</label>
                  <input className="form-input max-w-[200px]" type="number" value={form.diameter_mm ?? ''}
                    onChange={e => setForm(f => ({ ...f, diameter_mm: e.target.value ? Number(e.target.value) : null }))} placeholder="e.g. 900" />
                </div>
              )}
              {supportsRoundGlass(productType) && (
                <div className="flex items-end pb-2">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={!!form.round_glass}
                      onChange={e => {
                        const checked = e.target.checked;
                        setForm(f => ({
                          ...f,
                          round_glass: checked,
                          diameter_mm: checked ? (f.diameter_mm ?? null) : null,
                          glass_width: checked ? null : (f.glass_width ?? null),
                          glass_height: checked ? null : (f.glass_height ?? null),
                        }));
                      }}
                    />
                    Round Glass
                  </label>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div>
                <SearchableGlassValueSelect
                  value={(orderMeta.glass_side_one_value_id as string | null | undefined) ?? null}
                  onChange={(value) =>
                    setForm((f) => ({
                      ...f,
                      metadata: {
                        ...(f.metadata || {}),
                        glass_side_one_value_id: value,
                        patterned_gs_one_option_id: null,
                      },
                    }))
                  }
                  productType={productType || undefined}
                  placeholder="Search glass specifications..."
                  label="Glass Side One"
                  required={productType === 'single'}
                />
                <PatternedGlassOptionSelect
                  glassValueId={(orderMeta.glass_side_one_value_id as string | null | undefined) ?? null}
                  supportsPatterned={sideSupportsPatterned((orderMeta.glass_side_one_value_id as string | null | undefined) ?? null)}
                  value={(orderMeta.patterned_gs_one_option_id as string | null | undefined) ?? null}
                  rawFallbackLabel={((orderMeta.woo_patterned_glass as any)?.one as string | null | undefined) ?? null}
                  onChange={(pid) =>
                    setForm((f) => ({
                      ...f,
                      metadata: { ...(f.metadata || {}), patterned_gs_one_option_id: pid },
                    }))
                  }
                  label="Pattern (side one)"
                />
              </div>
              {(productType === 'double' || productType === 'triple') && (
                <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="form-label">Spacer Bar Type</label>
                    <select className="form-input" value={form.spacer_bar_type ?? ''}
                      onChange={e => setForm(f => ({ ...f, spacer_bar_type: (e.target.value || null) as any }))}>
                      <option value="">Select Spacer Type</option>
                      <option value="aluminum">Aluminum (Metal Bar)</option>
                      <option value="warm_edge">Warm Edge (Plastic Bar)</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Spacer Bar Colour</label>
                    <select className="form-input" value={form.spacer_bar_color ?? ''}
                      onChange={e => setForm(f => ({ ...f, spacer_bar_color: (e.target.value || null) as any }))}>
                      <option value="">Select Colour</option>
                      {allSpacerColors.map(c => (
                        <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Spacer Bar Thickness</label>
                    <select className="form-input" value={form.spacer_bar_thickness_mm ?? ''}
                      onChange={e => setForm(f => ({ ...f, spacer_bar_thickness_mm: e.target.value ? Number(e.target.value) : null }))}>
                      <option value="">Select Thickness</option>
                      {[4,6,8,10,12,14,16,18,20].map(n => <option key={n} value={n}>{n}mm</option>)}
                    </select>
                  </div>
                </div>
              )}
              {productType === 'triple' && (
                <div className="md:col-span-3">
                  <SearchableGlassValueSelect
                    value={(orderMeta.glass_side_three_value_id as string | null | undefined) ?? null}
                    onChange={(value) =>
                      setForm((f) => ({
                        ...f,
                        metadata: {
                          ...(f.metadata || {}),
                          glass_side_three_value_id: value,
                          middle_glass_panel_value_id: value,
                          patterned_gs_three_option_id: null,
                        },
                      }))
                    }
                    productType={productType || undefined}
                    placeholder="Search glass specifications..."
                    label="Glass Side Three"
                    required={productType === 'triple'}
                  />
                  <PatternedGlassOptionSelect
                    glassValueId={(orderMeta.glass_side_three_value_id as string | null | undefined) ?? null}
                    supportsPatterned={sideSupportsPatterned((orderMeta.glass_side_three_value_id as string | null | undefined) ?? null)}
                    value={(orderMeta.patterned_gs_three_option_id as string | null | undefined) ?? null}
                    rawFallbackLabel={((orderMeta.woo_patterned_glass as any)?.three as string | null | undefined) ?? null}
                    onChange={(pid) =>
                      setForm((f) => ({
                        ...f,
                        metadata: { ...(f.metadata || {}), patterned_gs_three_option_id: pid },
                      }))
                    }
                    label="Pattern (side three)"
                  />
                </div>
              )}
              {(productType === 'double' || productType === 'triple') && (
                <div className="md:col-span-3">
                  <SearchableGlassValueSelect
                    value={(orderMeta.glass_side_two_value_id as string | null | undefined) ?? null}
                    onChange={(value) =>
                      setForm((f) => ({
                        ...f,
                        metadata: {
                          ...(f.metadata || {}),
                          glass_side_two_value_id: value,
                          patterned_gs_two_option_id: null,
                        },
                      }))
                    }
                    productType={productType || undefined}
                    placeholder="Search glass specifications..."
                    label="Glass Side Two"
                    required={productType === 'double' || productType === 'triple'}
                  />
                  <PatternedGlassOptionSelect
                    glassValueId={(orderMeta.glass_side_two_value_id as string | null | undefined) ?? null}
                    supportsPatterned={sideSupportsPatterned((orderMeta.glass_side_two_value_id as string | null | undefined) ?? null)}
                    value={(orderMeta.patterned_gs_two_option_id as string | null | undefined) ?? null}
                    rawFallbackLabel={((orderMeta.woo_patterned_glass as any)?.two as string | null | undefined) ?? null}
                    onChange={(pid) =>
                      setForm((f) => ({
                        ...f,
                        metadata: { ...(f.metadata || {}), patterned_gs_two_option_id: pid },
                      }))
                    }
                    label="Pattern (side two)"
                  />
                </div>
              )}
              {productType === 'triple' && (
                <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="form-label">Spacer Bar Two Type</label>
                    <select className="form-input" value={form.spacer_bar2_type ?? ''}
                      onChange={e => setForm(f => ({ ...f, spacer_bar2_type: (e.target.value || null) as any }))}>
                      <option value="">Select Spacer Type</option>
                      <option value="aluminum">Aluminum (Metal Bar)</option>
                      <option value="warm_edge">Warm Edge (Plastic Bar)</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Spacer Bar Two Colour</label>
                    <select className="form-input" value={form.spacer_bar2_color ?? ''}
                      onChange={e => setForm(f => ({ ...f, spacer_bar2_color: (e.target.value || null) as any }))}>
                      <option value="">Select Colour</option>
                      {allSpacerColors.map(c => (
                        <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Spacer Bar Two Thickness</label>
                    <select className="form-input" value={form.spacer_bar2_thickness_mm ?? ''}
                      onChange={e => setForm(f => ({ ...f, spacer_bar2_thickness_mm: e.target.value ? Number(e.target.value) : null }))}>
                      <option value="">Select Thickness</option>
                      {[4,6,8,10,12,14,16,18,20].map(n => <option key={n} value={n}>{n}mm</option>)}
                    </select>
                  </div>
                </div>
              )}
              <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6 mt-2">
                {(productType === 'single' || productType === 'mirror') && (
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.polished_edges} onChange={e => setForm(f => ({ ...f, polished_edges: e.target.checked }))} /> Polished Edges</label>
                )}
                {(productType === 'single' || productType === 'mirror') && (
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.dubbed_corners} onChange={e => setForm(f => ({ ...f, dubbed_corners: e.target.checked }))} /> Dubbed Corners</label>
                )}
                {(productType === 'double' || productType === 'triple') && (
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.argon_filled} onChange={e => setForm(f => ({ ...f, argon_filled: e.target.checked }))} /> Argon Filled</label>
                )}
                {productType === 'mirror' && (
                  <>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.aluminium_backing_foil} onChange={e => setForm(f => ({ ...f, aluminium_backing_foil: e.target.checked }))} /> Aluminium Backing Foil</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.safety_backing} onChange={e => setForm(f => ({ ...f, safety_backing: e.target.checked }))} /> Safety Backing</label>
                  </>
                )}
              </div>
            </div>
          </ContentCard>

          <div className="flex flex-wrap gap-3">
            {(productType === 'single' || productType === 'mirror') && (
              <Button type="button" variant="secondary" onClick={() => { setShowDrilled(v => !v); }}>
                {showDrilled ? 'Hide' : 'Show'} Drilled Holes
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={() => { setCutoutShape((form.cutout_shape as any) || ''); setShowCutouts(v => !v); }}>{showCutouts ? 'Hide' : 'Show'} Hole cut out in glass</Button>
            {(productType === 'single' || productType === 'mirror') && (
              <Button type="button" variant="secondary" onClick={() => setShowRounded(v => !v)}>{showRounded ? 'Hide' : 'Show'} Rounded Corners</Button>
            )}
            {(productType === 'single' || productType === 'double' || productType === 'triple') && (
              <Button type="button" variant="secondary" onClick={() => setShowDecorative(v => !v)}>{showDecorative ? 'Hide' : 'Show'} Decorative Leads</Button>
            )}
            {(productType === 'double' || productType === 'triple') && (
              <>
                <Button type="button" variant="secondary" onClick={() => setShowDuplex(v => !v)}>{showDuplex ? 'Hide' : 'Show'} Duplex Bar</Button>
                <Button type="button" variant="secondary" onClick={() => setShowGeorgian(v => !v)}>{showGeorgian ? 'Hide' : 'Show'} Georgian Bar</Button>
              </>
            )}
            <Button type="button" variant="secondary" onClick={() => setShowQuoteDrawing(v => !v)}>{showQuoteDrawing ? 'Hide' : 'Show'} Quote Drawing</Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {(productType === 'single' || productType === 'mirror') && (
            <div>
              {showDrilled && (
                <ContentCard title="Drilled Holes">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="form-label">Hole Size (mm)</label>
                      <input className="form-input" type="number" value={form.hole_size_mm ?? ''}
                        onChange={e => setForm(f => ({ ...f, hole_size_mm: e.target.value ? Number(e.target.value) : null }))} />
                    </div>
                    <div>
                      <label className="form-label">Number of holes</label>
                      <input className="form-input" type="number" value={form.hole_count ?? ''}
                        onChange={e => setForm(f => ({ ...f, hole_count: e.target.value ? Number(e.target.value) : null }))} />
                    </div>
                    <div>
                      <label className="form-label">Hole Locations</label>
                      <textarea className="form-input" rows={3} value={form.hole_locations ?? ''}
                        onChange={e => setForm(f => ({ ...f, hole_locations: e.target.value || null }))} placeholder="E.g. Holes positioned 40mm from each edge" />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Has drawing image?</label>
                    <select className="form-input max-w-[200px]" value={hasDrilledImage} onChange={e => { const v = e.target.value as 'no'|'yes'; setHasDrilledImage(v); if (v === 'no') setPendingImages(prev => { const { drilled_holes, ...rest } = prev; return rest; }); }}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                    {hasDrilledImage === 'yes' && (
                      <div className="mt-3">
                        <FileUploadCard 
                          orderId={editingId} 
                          feature="drilled_holes" 
                          imageType="drawing" 
                          existingImage={getExistingImage('drilled_holes')}
                          onFileSelected={(file) => setPendingImages(prev => ({ ...prev, drilled_holes: file }))}
                        />
                      </div>
                    )}
                  </div>
                </ContentCard>
              )}
            </div>
            )}
            <div>
              {showCutouts && (
                <ContentCard title="Hole cut out in glass">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="form-label">Cut-out shape</label>
                      <select className="form-input" value={form.cutout_shape ?? ''}
                        onChange={e => { const v = e.target.value as any; setForm(f => ({ ...f, cutout_shape: v || null })); setCutoutShape(v); }}>
                        <option value="">Select Shape</option>
                        <option value="circle">Circle Cutout</option>
                        <option value="square">Square Cutout</option>
                        <option value="socket">Socket Cutout</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Number of Cutouts</label>
                      <input className="form-input" type="number" value={form.cutout_count ?? ''}
                        onChange={e => setForm(f => ({ ...f, cutout_count: e.target.value ? Number(e.target.value) : null }))} />
                    </div>
                    {cutoutShape === 'circle' && (
                      <>
                        <div>
                          <label className="form-label">Cut-out Diameter (mm)</label>
                          <input className="form-input" type="number" value={form.cutout_diameter_mm ?? ''}
                            onChange={e => setForm(f => ({ ...f, cutout_diameter_mm: e.target.value ? Number(e.target.value) : null }))} />
                        </div>
                        <div>
                          <label className="form-label">Cut-out Position</label>
                          <input className="form-input" value={form.cutout_position ?? ''}
                            onChange={e => setForm(f => ({ ...f, cutout_position: e.target.value || null }))} />
                        </div>
                      </>
                    )}
                    {(cutoutShape === 'square' || cutoutShape === 'socket') && (
                      <>
                        <div>
                          <label className="form-label">Cut-out Width (mm)</label>
                          <input className="form-input" type="number" value={form.cutout_width_mm ?? ''}
                            onChange={e => setForm(f => ({ ...f, cutout_width_mm: e.target.value ? Number(e.target.value) : null }))} />
                        </div>
                        <div>
                          <label className="form-label">Cut-out Height (mm)</label>
                          <input className="form-input" type="number" value={form.cutout_height_mm ?? ''}
                            onChange={e => setForm(f => ({ ...f, cutout_height_mm: e.target.value ? Number(e.target.value) : null }))} />
                        </div>
                        {cutoutShape === 'square' && (
                          <div>
                            <label className="form-label">Cut-out Position</label>
                            <input className="form-input" value={form.cutout_position ?? ''}
                              onChange={e => setForm(f => ({ ...f, cutout_position: e.target.value || null }))} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div>
                    <label className="form-label">Has cutout image?</label>
                    <select className="form-input max-w-[200px]" value={hasCutoutImage} onChange={e => { const v = e.target.value as 'no'|'yes'; setHasCutoutImage(v); if (v === 'no') setPendingImages(prev => { const { cutout, ...rest } = prev; return rest; }); }}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                    {hasCutoutImage === 'yes' && (
                      <div className="mt-3">
                        <FileUploadCard 
                          orderId={editingId} 
                          feature="cutout" 
                          imageType="cutout" 
                          existingImage={getExistingImage('cutout')}
                          onFileSelected={(file) => setPendingImages(prev => ({ ...prev, cutout: file }))}
                        />
                      </div>
                    )}
                  </div>
                </ContentCard>
              )}
            </div>
            {(productType === 'single' || productType === 'mirror') && (
            <div>
              {showRounded && (
                <ContentCard title="Rounded Corners">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="form-label">Corner radius (mm)</label>
                      <input className="form-input" type="number" value={form.rounded_corner_radius_mm ?? ''}
                        onChange={e => setForm(f => ({ ...f, rounded_corner_radius_mm: e.target.value ? Number(e.target.value) : null }))} />
                    </div>
                    <div>
                      <label className="form-label">How many radius corners</label>
                      <select className="form-input" value={form.rounded_corner_count ?? ''}
                        onChange={e => setForm(f => ({ ...f, rounded_corner_count: e.target.value ? Number(e.target.value) : null }))}>
                        <option value="">Select how many corners</option>
                        {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Corner Locations</label>
                      <input className="form-input" value={form.rounded_corner_locations ?? ''}
                        onChange={e => setForm(f => ({ ...f, rounded_corner_locations: e.target.value || null }))} />
                    </div>
                    <div>
                      <label className="form-label">Has drawing image?</label>
                      <select className="form-input max-w-[200px]" value={hasRoundedImage} onChange={e => { const v = e.target.value as 'no'|'yes'; setHasRoundedImage(v); if (v === 'no') setPendingImages(prev => { const { rounded_corners, ...rest } = prev; return rest; }); }}>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                      {hasRoundedImage === 'yes' && (
                        <div className="mt-3">
                          <FileUploadCard 
                            orderId={editingId} 
                            feature="rounded_corners" 
                            imageType="drawing" 
                            existingImage={getExistingImage('rounded_corners')}
                            onFileSelected={(file) => setPendingImages(prev => ({ ...prev, rounded_corners: file }))}
                          />
                        </div>
                      )}
                    </div>
                    
                  </div>
                </ContentCard>
              )}
            </div>
            )}
            {(productType === 'single' || productType === 'double' || productType === 'triple') && (
              <>
                <div>
                  {showDecorative && (
                    <ContentCard title="Decorative Leads">
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="form-label">Leaded Pattern</label>
                          <select className="form-input" value={form.decorative_leaded_pattern ?? ''}
                            onChange={e => setForm(f => ({ ...f, decorative_leaded_pattern: (e.target.value || null) as any }))}>
                            <option value="">Select lead pattern</option>
                            <option value="square">Square</option>
                            <option value="diamond">Diamond</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Lead colour</label>
                          <select className="form-input" value={form.decorative_lead_color ?? ''}
                            onChange={e => setForm(f => ({ ...f, decorative_lead_color: (e.target.value || null) as any }))}>
                            <option value="">Select lead colour</option>
                            <option value="silver">Silver Lead</option>
                            <option value="antique">Antique Lead</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Lead Thickness</label>
                          <select className="form-input" value={form.decorative_lead_thickness_mm ?? ''}
                            onChange={e => setForm(f => ({ ...f, decorative_lead_thickness_mm: e.target.value ? Number(e.target.value) : null }))}>
                            <option value="">Select lead thickness</option>
                            {[6,9,12].map(n => <option key={n} value={n}>{n}mm Lead</option>)}
                          </select>
                        </div>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.decorative_lead_soldering_required} onChange={e => setForm(f => ({ ...f, decorative_lead_soldering_required: e.target.checked }))} /> Soldering required</label>
                        <div>
                          <label className="form-label">Has drawing image?</label>
                          <select className="form-input max-w-[200px]" value={hasDecorativeImage} onChange={e => { const v = e.target.value as 'no'|'yes'; setHasDecorativeImage(v); if (v === 'no') setPendingImages(prev => { const { decorative_leads, ...rest } = prev; return rest; }); }}>
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                          </select>
                          {hasDecorativeImage === 'yes' && (
                            <div className="mt-3">
                              <FileUploadCard 
                                orderId={editingId} 
                                feature="decorative_leads" 
                                imageType="drawing" 
                                existingImage={getExistingImage('decorative_leads')}
                                onFileSelected={(file) => setPendingImages(prev => ({ ...prev, decorative_leads: file }))}
                              />
                            </div>
                          )}
                        </div>
                        
                      </div>
                    </ContentCard>
                  )}
                </div>
                <div>
                  {showDuplex && (
                    <ContentCard title="Duplex Bar">
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="form-label">Duplex Bar Type</label>
                          <select className="form-input" value={form.duplex_bar_type ?? ''}
                            onChange={e => setForm(f => ({ ...f, duplex_bar_type: (e.target.value || null) as any }))}>
                            <option value="">Select Duplex Bar Type</option>
                            <option value="single">Single Duplex Bar</option>
                            <option value="grid">Grid Duplex Bar</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Duplex Thickness</label>
                          <select className="form-input" value={form.duplex_bar_thickness ?? ''}
                            onChange={e => setForm(f => ({ ...f, duplex_bar_thickness: (e.target.value || null) as any }))}>
                            <option value="">Select Duplex Thickness</option>
                            <option value="18mm">18mm Duplex Bar</option>
                            <option value="24mm">24mm Duplex Bar</option>
                            <option value="b2b_spacer_14mm">back to Back Spacer (14mm)</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Has drawing image?</label>
                          <select className="form-input max-w-[200px]" value={hasDuplexImage} onChange={e => { const v = e.target.value as 'no'|'yes'; setHasDuplexImage(v); if (v === 'no') setPendingImages(prev => { const { duplex_bar, ...rest } = prev; return rest; }); }}>
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                          </select>
                          {hasDuplexImage === 'yes' && (
                            <div className="mt-3">
                              <FileUploadCard 
                                orderId={editingId} 
                                feature="duplex_bar" 
                                imageType="drawing" 
                                existingImage={getExistingImage('duplex_bar')}
                                onFileSelected={(file) => setPendingImages(prev => ({ ...prev, duplex_bar: file }))}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </ContentCard>
                  )}
                </div>
                <div>
                  {showGeorgian && (
                    <ContentCard title="Georgian Bar">
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="form-label">Georgian Bar Type</label>
                          <select className="form-input" value={form.georgian_bar_type ?? ''}
                            onChange={e => setForm(f => ({ ...f, georgian_bar_type: (e.target.value || null) as any }))}>
                            <option value="">Select Georgian Bar Type</option>
                            <option value="single">Single Georgian Bar</option>
                            <option value="grid">Grid Georgian Bar</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Georgian Bar Thickness</label>
                          <select className="form-input" value={form.georgian_bar_thickness ?? ''}
                            onChange={e => setForm(f => ({ ...f, georgian_bar_thickness: (e.target.value || null) as any }))}>
                            <option value="">Select Duplex Thickness</option>
                            <option value="18mm">18mm Georgian Bar</option>
                            <option value="25mm">25mm Georgian Bar</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Has drawing image?</label>
                          <select className="form-input max-w-[200px]" value={hasGeorgianImage} onChange={e => { const v = e.target.value as 'no'|'yes'; setHasGeorgianImage(v); if (v === 'no') setPendingImages(prev => { const { georgian_bar, ...rest } = prev; return rest; }); }}>
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                          </select>
                          {hasGeorgianImage === 'yes' && (
                            <div className="mt-3">
                              <FileUploadCard 
                                orderId={editingId} 
                                feature="georgian_bar" 
                                imageType="drawing" 
                                existingImage={getExistingImage('georgian_bar')}
                                onFileSelected={(file) => setPendingImages(prev => ({ ...prev, georgian_bar: file }))}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </ContentCard>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Quote Drawing Card - Always visible for all product types */}
          {showQuoteDrawing && (
            <ContentCard title="Quote Drawing">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="form-label">Has drawing?</label>
                  <select 
                    className="form-input max-w-[200px]" 
                    value={hasQuoteDrawingImage} 
                    onChange={e => { 
                      const v = e.target.value as 'no'|'yes'; 
                      setHasQuoteDrawingImage(v); 
                      if (v === 'no') {
                        setPendingImages(prev => { 
                          const { quote_drawing, ...rest } = prev; 
                          return rest; 
                        }); 
                      }
                    }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  {hasQuoteDrawingImage === 'yes' && (
                    <div className="mt-3">
                      <FileUploadCard 
                        orderId={editingId} 
                        feature="quote_drawing" 
                        imageType="drawing" 
                        existingImage={getExistingImage('quote_drawing')}
                        onFileSelected={(file) => setPendingImages(prev => ({ ...prev, quote_drawing: file }))}
                      />
                    </div>
                  )}
                </div>
              </div>
            </ContentCard>
          )}

          <ContentCard title="Order Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Quantity</label>
                <input className="form-input" type="number" min={1} value={form.quantity ?? 1}
                  onChange={e => setForm(f => ({ ...f, quantity: Number(e.target.value) }))} />
              </div>
              {/* Rack field now managed via inline table editing */}
            </div>
          </ContentCard>
          <ContentCard title="References and Notes">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">References</label>
                <input className="form-input" value={form.reference ?? ''}
                  onChange={e => setForm(f => ({ ...f, reference: e.target.value || null }))} />
              </div>
              <div>
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows={3} value={form.notes ?? ''}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value || null }))} />
              </div>
            </div>
          </ContentCard>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Button onClick={onSave} disabled={!productType || isCreating || isUpdatingOrder}>{editingId ? 'Update Order' : 'Save Order'}</Button>
        {editingId && (<Button variant="secondary" onClick={resetForm}>Cancel Edit</Button>)}
        {editingId && (<Button variant="secondary" onClick={resetForm}>Create New Order</Button>)}
      </div>
      <div className="mt-6">
        <div className="text-sm font-semibold mb-2">Orders for this Job</div>
        <OrderDetailsTable
          orders={orders}
          loading={isOrdersLoading}
          mode="edit"
          rackContext="orders_edit"
          onEdit={handleEditOrder}
          onDelete={handleDeleteOrder}
        />
      </div>
      
      {/* Move to Production Section */}
      {orders.length > 0 && job?.status !== 'production' && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Ready for Production?</h4>
              <p className="text-sm text-gray-600 mt-1">
                Move this job to production workflow with {orders.length} order{orders.length !== 1 ? 's' : ''}
              </p>
            </div>
            <MoveToProductionButton 
              jobId={jobId} 
              disabled={orders.length === 0}
              onSuccess={() => {
                toast.success('Job moved to production', 'Job is now in production workflow');
                // Optionally refetch job data to show updated status
              }}
            />
          </div>
        </div>
      )}
    </ContentCard>
  );
};




